/**
 * Self-service смена пароля (зеркало uspto_scraper). Поля: текущий / новый /
 * подтверждение. Локальная валидация: новый ≥ 8, совпадает с подтверждением и
 * отличается от текущего. Серверная ошибка (неверный текущий пароль) — в alert.
 * После успеха все refresh-токены отзываются — на других устройствах разлогин.
 */
import React, { useState } from 'react';
import { Modal, Form, Input, Button, App } from 'antd';
import { useTranslation } from 'react-i18next';
import { changePassword } from '../api/client';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values: {
    current_password: string;
    new_password: string;
  }) => {
    setLoading(true);
    try {
      await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      message.success(t('idea_calc.change_password.success'));
      handleClose();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        t('idea_calc.change_password.err_generic');
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('idea_calc.change_password.title')}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark={false}>
        <Form.Item
          name="current_password"
          label={t('idea_calc.change_password.current')}
          rules={[{ required: true, message: t('idea_calc.change_password.current_required') }]}
        >
          <Input.Password autoComplete="current-password" autoFocus />
        </Form.Item>

        <Form.Item
          name="new_password"
          label={t('idea_calc.change_password.new')}
          rules={[
            { required: true, message: t('idea_calc.change_password.new_required') },
            { min: 8, message: t('idea_calc.change_password.err_min') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value && value === getFieldValue('current_password')) {
                  return Promise.reject(new Error(t('idea_calc.change_password.err_same')));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder={t('idea_calc.change_password.hint_min')}
          />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label={t('idea_calc.change_password.confirm')}
          dependencies={['new_password']}
          rules={[
            { required: true, message: t('idea_calc.change_password.confirm_required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || value === getFieldValue('new_password')) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('idea_calc.change_password.err_match')));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={handleClose} style={{ marginRight: 8 }}>
            {t('idea_calc.change_password.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('idea_calc.change_password.submit')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
